/**
 * Text Utilities
 *
 * Utility functions for text processing, including diacritics normalization
 * for accent-insensitive searching/filtering.
 */

/**
 * Normalizes text by removing diacritics/accents for accent-insensitive comparison.
 * Converts characters like ö, é, ñ to their base forms (o, e, n).
 *
 * Uses Unicode normalization (NFD) to decompose characters into base + combining marks,
 * then removes the combining diacritical marks.
 *
 * @example
 * normalizeText("Köln") // returns "koln"
 * normalizeText("café") // returns "cafe"
 * normalizeText("señor") // returns "senor"
 * normalizeText("naïve") // returns "naive"
 */
export function normalizeText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/**
 * Checks if the search term is found in the text, ignoring diacritics and case.
 *
 * @example
 * matchesSearch("Köln", "koln") // returns true
 * matchesSearch("Köln", "kol") // returns true
 * matchesSearch("café", "cafe") // returns true
 * matchesSearch("Müller", "muller") // returns true
 */
export function matchesSearch(text: string, searchTerm: string): boolean {
  if (!searchTerm) return true;
  return normalizeText(text).includes(normalizeText(searchTerm));
}
